import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { useCurrency } from '../contexts/CurrencyContext';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import OrderInvoice from '../components/Invoice/OrderInvoice';
import ShippingLabel from '../components/Invoice/ShippingLabel';
import {
  ArrowLeftIcon,
  MapPinIcon,
  CreditCardIcon,
  CubeIcon,
  CheckCircleIcon,
  XMarkIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  PrinterIcon,
  TagIcon
} from '@heroicons/react/24/outline';

const AdminOrderDetail = () => {
  const { id: paramId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState(new Set());
  const [itemsNeedingClarification, setItemsNeedingClarification] = useState(new Set());
  const [itemNotes, setItemNotes] = useState({});
  const [showInvoice, setShowInvoice] = useState(false);
  const [showLabel, setShowLabel] = useState(false);
  const [orderHistory, setOrderHistory] = useState(null);
  const { formatPrice } = useCurrency();
  const { isAuthenticated, user, getToken } = useAuth();
  const API_BASE = process.env.REACT_APP_API_URL || '';

  // Extract ID from URL pathname if not in params (fallback for DashboardRouter)
  const id = paramId || (() => {
    const pathMatch = location.pathname.match(/\/dashboard\/orders\/(\d+)/);
    return pathMatch ? pathMatch[1] : null;
  })();

  const fetchOrder = useCallback(async () => {
    if (!id) {
      console.error('❌ [AdminOrderDetail] Cannot fetch order: ID is missing');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = getToken(true); // Dashboard token
      if (!token) {
        navigate('/dashboard/login');
        return;
      }

      // Use relative URL if API_BASE is empty (when served by same server)
      const orderUrl = API_BASE ? `${API_BASE}/api/orders/admin/${id}` : `/api/orders/admin/${id}`;
      console.log('🔍 [AdminOrderDetail] Fetching order from:', orderUrl);
      console.log('🔍 [AdminOrderDetail] Order ID:', id);
      
      const response = await fetch(orderUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('Order response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to fetch order' }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('📦 [AdminOrderDetail] Order data received:', JSON.stringify(data, null, 2));
      
      if (data.success && data.data && data.data.order) {
        const orderData = data.data.order;
        console.log('📦 [AdminOrderDetail] Order items:', orderData.items?.length || 0);
        console.log('📦 [AdminOrderDetail] Order status:', orderData.status);
        console.log('📦 [AdminOrderDetail] Order total:', orderData.total_amount);
        
        // Ensure items is always an array and normalize image URLs
        if (!Array.isArray(orderData.items)) {
          orderData.items = [];
        } else {
          // Log raw items before processing
          console.log('📦 [AdminOrderDetail] Raw order items from API:', JSON.stringify(orderData.items, null, 2));
          
          // Normalize image URLs to prevent flickering
          orderData.items = orderData.items.map(item => {
            const normalizedItem = {
              ...item,
              // Use a single consistent image URL field
              image_url: item.image_url || item.product_image || null,
              product_image: item.product_image || item.image_url || null
            };
            // Log product details for debugging
            console.log('📦 [AdminOrderDetail] Processing item:', {
              id: normalizedItem.id,
              product_id: normalizedItem.product_id,
              barcode: normalizedItem.barcode,
              stockcode: normalizedItem.stockcode,
              brand: normalizedItem.brand,
              department: normalizedItem.department,
              category_name: normalizedItem.category_name,
              stock_quantity: normalizedItem.stock_quantity,
              original_price: normalizedItem.original_price,
              discount: normalizedItem.discount,
              vat_percentage: normalizedItem.vat_percentage,
              // Log all keys to see what's available
              allKeys: Object.keys(normalizedItem)
            });
            return normalizedItem;
          });
        }
        
        setOrder(orderData);
        setSelectedStatus((orderData.status || 'PENDING').toUpperCase());
      } else {
        console.error('❌ [AdminOrderDetail] Invalid response format:', data);
        throw new Error(data.message || 'Invalid response format');
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error(error.message || 'Failed to load order details. Please try again.');
      // Don't navigate immediately, let user see the error
      setTimeout(() => navigate('/dashboard/orders'), 2000);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, getToken, id, navigate]);

  const fetchHistory = useCallback(async () => {
    if (!id) return;
    try {
      const token = getToken(true);
      const response = await fetch(`${API_BASE}/api/orders/${id}/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.success) setOrderHistory(data.data);
    } catch (error) {
      console.error('Error fetching order history:', error);
    }
  }, [API_BASE, getToken, id]);

  useEffect(() => {
    if (!id) {
      console.error('❌ [AdminOrderDetail] No order ID found in URL');
      toast.error('Invalid order ID');
      navigate('/dashboard/orders');
      return;
    }

    if (isAuthenticated && (user?.role === 'admin' || user?.role === 'employee')) {
      fetchOrder();
      fetchHistory();
    } else if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [fetchOrder, fetchHistory, id, isAuthenticated, navigate, user?.role]);

  const handleStatusUpdate = async () => {
    if (!selectedStatus || selectedStatus === order.status) {
      toast.error('Please select a different status');
      return;
    }

    try {
      setUpdating(true);
      const token = getToken(true); // Dashboard token
      
      const response = await fetch(`${API_BASE}/api/orders/${id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: selectedStatus })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Order status updated successfully');
        setOrder(prev => ({ ...prev, status: selectedStatus }));
        fetchHistory();
      } else {
        toast.error(data.message || 'Failed to update order status');
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const getStatusColor = (status) => {
    const statusUpper = (status || '').toUpperCase();
    switch (statusUpper) {
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'CONFIRMED':
      case 'PROCESSING':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'PACKING':
        return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'SHIPPED':
      case 'DISPATCHED':
        return 'bg-indigo-100 text-indigo-800 border-indigo-300';
      case 'OUT_FOR_DELIVERY':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'DELIVERED':
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'RETURNED':
      case 'REFUNDED':
        return 'bg-pink-100 text-pink-800 border-pink-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'PENDING';
    return status.replace(/_/g, ' ');
  };

  // Order status flow with timeline
  const getOrderStatusFlow = () => {
    const statusUpper = (orderStatus || 'PENDING').toUpperCase();
    const flow = [
      { 
        status: 'PENDING', 
        label: 'Order Placed', 
        description: 'Customer places the order',
        icon: '📝',
        completed: true
      },
      { 
        status: 'CONFIRMED', 
        label: 'Order Confirmation', 
        description: 'Admin reviews and confirms order',
        icon: '✅',
        completed: ['CONFIRMED', 'PROCESSING', 'PACKING', 'SHIPPED', 'DISPATCHED', 'OUT_FOR_DELIVERY', 'DELIVERED', 'COMPLETED'].includes(statusUpper)
      },
      { 
        status: 'PACKING', 
        label: 'Order Preparation', 
        description: 'Items are packed and prepared',
        icon: '📦',
        completed: ['PACKING', 'SHIPPED', 'DISPATCHED', 'OUT_FOR_DELIVERY', 'DELIVERED', 'COMPLETED'].includes(statusUpper)
      },
      { 
        status: 'SHIPPED', 
        label: 'Shipping / Dispatch', 
        description: 'Order handed over to delivery partner',
        icon: '🚚',
        completed: ['SHIPPED', 'DISPATCHED', 'OUT_FOR_DELIVERY', 'DELIVERED', 'COMPLETED'].includes(statusUpper)
      },
      { 
        status: 'OUT_FOR_DELIVERY', 
        label: 'Out for Delivery', 
        description: 'Delivery partner is delivering',
        icon: '🏃',
        completed: ['OUT_FOR_DELIVERY', 'DELIVERED', 'COMPLETED'].includes(statusUpper)
      },
      { 
        status: 'DELIVERED', 
        label: 'Order Delivered', 
        description: 'Customer receives the order',
        icon: '🎉',
        completed: ['DELIVERED', 'COMPLETED'].includes(statusUpper)
      }
    ];

    // Mark current status - handle PROCESSING as CONFIRMED, DISPATCHED as SHIPPED, COMPLETED as DELIVERED
    flow.forEach((step, index) => {
      if (step.status === statusUpper) {
        step.current = true;
      } else if (statusUpper === 'PROCESSING' && step.status === 'CONFIRMED') {
        step.current = true;
      } else if (statusUpper === 'DISPATCHED' && step.status === 'SHIPPED') {
        step.current = true;
      } else if (statusUpper === 'COMPLETED' && step.status === 'DELIVERED') {
        step.current = true;
      }
    });

    // Handle exception statuses
    if (['CANCELLED', 'RETURNED', 'REFUNDED'].includes(statusUpper)) {
      flow.push({
        status: statusUpper,
        label: statusUpper === 'CANCELLED' ? 'Order Cancelled' : statusUpper === 'RETURNED' ? 'Order Returned' : 'Order Refunded',
        description: statusUpper === 'CANCELLED' ? 'Order cancelled by customer or admin' : 'Item returned and refund processed',
        icon: statusUpper === 'CANCELLED' ? '❌' : '↩️',
        completed: true,
        current: true,
        isException: true
      });
    }

    return flow;
  };

  const formatDuration = (seconds) => {
    if (seconds == null) return null;
    const totalMinutes = Math.round(seconds / 60);
    if (totalMinutes < 1) return '<1 min';
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours === 0) return `${minutes} min`;
    if (minutes === 0) return `${hours} hr`;
    return `${hours} hr ${minutes} min`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const parseShippingAddress = (address) => {
    if (!address) return 'N/A';
    if (typeof address === 'string') {
      try {
        return JSON.parse(address);
      } catch (e) {
        return address;
      }
    }
    return address;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center bg-white p-12 rounded-2xl shadow-xl max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Order Not Found</h2>
          <p className="text-gray-600 mb-8">The order you're looking for doesn't exist.</p>
          <Link
            to="/dashboard/orders"
            className="inline-block bg-indigo-600 text-white px-8 py-3 rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
          >
            Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  const shippingAddress = parseShippingAddress(order.shipping_address);
  const orderItems = Array.isArray(order.items) ? order.items : [];
  const orderStatus = (order.status || 'PENDING').toUpperCase();
  
  console.log('📦 [AdminOrderDetail] Rendering order:', {
    id: order.id,
    order_number: order.order_number,
    items_count: orderItems.length,
    status: orderStatus,
    total_amount: order.total_amount
  });

  return (
    <div className="p-6">
      {/* Invoice Modal */}
      {showInvoice && (
        <OrderInvoice
          order={order}
          onClose={() => setShowInvoice(false)}
        />
      )}

      {/* Shipping Label Modal */}
      {showLabel && (
        <ShippingLabel
          order={order}
          onClose={() => setShowLabel(false)}
        />
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <Link
            to="/dashboard/orders"
            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-semibold"
          >
            <ArrowLeftIcon className="w-5 h-5" />
            Back to Orders
          </Link>
          <div className="flex items-center gap-3">
            {order.delivery_type !== 'STORE_PICKUP' && (
              <button
                onClick={() => setShowLabel(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border-2 border-indigo-600 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors font-semibold shadow-sm"
                title="Print a small shipping sticker for the package"
              >
                <TagIcon className="w-5 h-5" />
                Print Label
              </button>
            )}
            <button
              onClick={() => setShowInvoice(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold shadow-md hover:shadow-lg"
            >
              <PrinterIcon className="w-5 h-5" />
              View & Print Invoice
            </button>
          </div>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Order #{order.order_number || order.id}
        </h1>
        <p className="text-gray-600">Placed on {formatDate(order.created_at)}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Status Timeline */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Order Status Timeline</h2>
            <div className="space-y-4">
              {getOrderStatusFlow().map((step, index) => {
                const isCompleted = step.completed && !step.current && !step.isException;
                const isCurrent = step.current && !step.isException;
                const isException = step.isException;
                
                return (
                  <div key={step.status} className="flex items-start gap-4">
                    {/* Timeline Line */}
                    <div className="flex flex-col items-center">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${
                        isException 
                          ? 'bg-red-100 text-red-600 border-2 border-red-300'
                          : isCurrent
                          ? 'bg-indigo-100 text-indigo-600 border-2 border-indigo-500 ring-2 ring-indigo-200'
                          : isCompleted
                          ? 'bg-green-100 text-green-600 border-2 border-green-300'
                          : 'bg-gray-100 text-gray-400 border-2 border-gray-300'
                      }`}>
                        {step.icon}
                      </div>
                      {index < getOrderStatusFlow().length - 1 && (
                        <div className={`w-0.5 h-16 mt-2 ${
                          isCompleted ? 'bg-green-300' : 'bg-gray-200'
                        }`} />
                      )}
                    </div>
                    
                    {/* Status Details */}
                    <div className="flex-1 pb-8">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className={`text-lg font-semibold ${
                          isException 
                            ? 'text-red-700'
                            : isCurrent
                            ? 'text-indigo-700'
                            : isCompleted
                            ? 'text-green-700'
                            : 'text-gray-500'
                        }`}>
                          {step.label}
                        </h3>
                        {isCurrent && (
                          <span className="px-2 py-1 text-xs font-bold bg-indigo-100 text-indigo-700 rounded-full">
                            CURRENT
                          </span>
                        )}
                        {isCompleted && !isCurrent && (
                          <span className="px-2 py-1 text-xs font-bold bg-green-100 text-green-700 rounded-full">
                            ✓ COMPLETED
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{step.description}</p>
                      {isCurrent && (
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(orderStatus)}`}>
                          {formatStatus(orderStatus)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Real stage-by-stage timestamps and durations, separate from the visual
              flow above (which maps onto a fixed 6-step template) - this shows exactly
              what happened and when, in whatever statuses the order actually passed
              through. */}
          {orderHistory && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-xl font-bold text-gray-900">Fulfilment Time</h2>
                {orderHistory.historical_estimate && (
                  <span className="px-2 py-0.5 text-xs font-semibold bg-gray-100 text-gray-500 rounded-full">Estimated</span>
                )}
              </div>
              {orderHistory.historical_estimate && (
                <p className="text-xs text-gray-500 mb-4">
                  This order was placed before per-stage tracking was added, so only the placement time and when it reached its current status are known - not each stage in between.
                </p>
              )}
              <ul className="space-y-2 text-sm">
                <li className="flex items-center justify-between border-b border-gray-100 pb-2">
                  <span className="font-medium text-gray-900">Placed</span>
                  <span className="text-gray-600">{formatDate(orderHistory.placed_at)}</span>
                </li>
                {orderHistory.history.map((entry, index) => (
                  <li key={index} className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-0">
                    <span className="font-medium text-gray-900">{formatStatus(entry.to_status)}</span>
                    <span className="text-gray-600">
                      {formatDate(entry.changed_at)}
                      {entry.duration_in_previous_status_seconds != null && (
                        <span className="ml-2 text-gray-400">
                          ({formatDuration(entry.duration_in_previous_status_seconds)} in {formatStatus(entry.from_status || 'previous stage')})
                        </span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              {['DELIVERED', 'COMPLETED'].includes(orderHistory.current_status) && orderHistory.history.length > 0 && (
                <p className="mt-4 text-sm font-semibold text-gray-900">
                  Total fulfilment time: {formatDuration(
                    (new Date(orderHistory.history[orderHistory.history.length - 1].changed_at).getTime() - new Date(orderHistory.placed_at).getTime()) / 1000
                  )}
                </p>
              )}
            </div>
          )}

          {/* Status Update Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Update Order Status</h2>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">Change Status To</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="PENDING">Pending - Order Placed</option>
                  <option value="CONFIRMED">Confirmed - Order Confirmed</option>
                  <option value="PROCESSING">Processing - Order Processing</option>
                  <option value="PACKING">Packing - Order Preparation</option>
                  <option value="SHIPPED">Shipped - Shipping / Dispatch</option>
                  <option value="DISPATCHED">Dispatched - Order Dispatched</option>
                  <option value="OUT_FOR_DELIVERY">Out for Delivery</option>
                  <option value="DELIVERED">Delivered - Order Delivered</option>
                  <option value="COMPLETED">Completed - Order Completed</option>
                  <option value="CANCELLED">Cancelled - Order Cancelled</option>
                  <option value="RETURNED">Returned - Order Returned</option>
                  <option value="REFUNDED">Refunded - Order Refunded</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleStatusUpdate}
                  disabled={updating || selectedStatus === orderStatus}
                  className="w-full sm:w-auto px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold flex items-center justify-center gap-2"
                >
                  {updating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-5 h-5" />
                      Update Status
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <span className="text-sm text-gray-600">Current Status: </span>
              <span
                className={`px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(
                  orderStatus
                )}`}
              >
                {formatStatus(orderStatus)}
              </span>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Customer Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Name</p>
                <p className="font-semibold text-gray-900">{order.customer_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Email</p>
                <p className="font-semibold text-gray-900">{order.email || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Phone</p>
                <p className="font-semibold text-gray-900">{order.phone || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <CubeIcon className="w-6 h-6 text-indigo-600" />
                Order Items ({orderItems.length})
              </h2>
              {selectedItemIds.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">
                    {selectedItemIds.size} item{selectedItemIds.size > 1 ? 's' : ''} selected
                  </span>
                  <button
                    onClick={() => setSelectedItemIds(new Set())}
                    className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Clear Selection
                  </button>
                </div>
              )}
            </div>
            <div className="space-y-4">
              {orderItems.length > 0 ? (
                orderItems.map((item) => {
                  const imageUrl = item.image_url || item.product_image || '/api/placeholder/80/80';
                  const isSelected = selectedItemIds.has(item.id);
                  const needsClarification = itemsNeedingClarification.has(item.id);
                  
                  return (
                    <div
                      key={item.id}
                      className={`flex items-start gap-4 p-4 border-2 rounded-lg transition-all group ${
                        isSelected
                          ? 'border-indigo-500 bg-indigo-50'
                          : needsClarification
                          ? 'border-yellow-400 bg-yellow-50'
                          : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                      }`}
                    >
                      {/* Checkbox for Selection */}
                      <div 
                        className="flex flex-col items-center gap-2 pt-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            const newSelected = new Set(selectedItemIds);
                            if (e.target.checked) {
                              newSelected.add(item.id);
                            } else {
                              newSelected.delete(item.id);
                            }
                            setSelectedItemIds(newSelected);
                          }}
                          className="w-5 h-5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer"
                          title="Select item"
                        />
                        {/* Clarification Required Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const newClarification = new Set(itemsNeedingClarification);
                            if (needsClarification) {
                              newClarification.delete(item.id);
                              // Remove notes when unflagging
                              const newNotes = { ...itemNotes };
                              delete newNotes[item.id];
                              setItemNotes(newNotes);
                            } else {
                              newClarification.add(item.id);
                            }
                            setItemsNeedingClarification(newClarification);
                          }}
                          className={`p-1.5 rounded-full transition-colors ${
                            needsClarification
                              ? 'bg-yellow-500 text-white'
                              : 'bg-gray-200 text-gray-600 hover:bg-yellow-200'
                          }`}
                          title={needsClarification ? 'Remove clarification flag' : 'Mark as needs clarification'}
                        >
                          <ExclamationTriangleIcon className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Item Image */}
                      <div className="relative">
                        <div className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                          <img
                            key={`${item.id}-${imageUrl}`}
                            src={imageUrl}
                            alt={item.name || item.product_name || 'Product'}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => {
                              if (!e.target.dataset.errorHandled) {
                                e.target.dataset.errorHandled = 'true';
                                e.target.src = '/api/placeholder/80/80';
                              }
                            }}
                          />
                        </div>
                        <div className="absolute top-1 right-1 bg-indigo-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <InformationCircleIcon className="w-4 h-4" />
                        </div>
                      </div>

                      {/* Item Details - Clickable Area */}
                      <div 
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => {
                          setSelectedItem(item);
                          setShowItemModal(true);
                        }}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="font-semibold text-gray-900">
                                {item.name || item.product_name || `Product #${item.product_id || 'N/A'}`}
                              </h3>
                              {item.variant_label && (
                                <span className="text-xs text-gray-500">{item.variant_label}</span>
                              )}
                              {isSelected && (
                                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full flex items-center gap-1">
                                  <CheckIcon className="w-3 h-3" />
                                  Selected
                                </span>
                              )}
                              {needsClarification && (
                                <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full flex items-center gap-1">
                                  <ExclamationTriangleIcon className="w-3 h-3" />
                                  Needs Check
                                </span>
                              )}
                            </div>
                            {item.description && (
                              <p className="text-sm text-gray-600 line-clamp-2 mb-2">{item.description}</p>
                            )}
                            <div className="flex flex-wrap gap-4 mt-1">
                              <p className="text-sm text-gray-500">
                                <span className="font-medium">Quantity:</span> {item.quantity || 0}
                              </p>
                              {item.product_id && (
                                <p className="text-sm text-gray-500">
                                  <span className="font-medium">Product ID:</span> {item.product_id}
                                </p>
                              )}
                              <p className="text-sm text-gray-500">
                                <span className="font-medium">Item ID:</span> {item.id}
                              </p>
                            </div>
                            {needsClarification && (
                              <div className="mt-3">
                                <label className="block text-xs font-medium text-yellow-700 mb-1">
                                  Clarification Notes:
                                </label>
                                <textarea
                                  placeholder="Add notes about what needs clarification or checking..."
                                  value={itemNotes[item.id] || ''}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    setItemNotes(prev => ({
                                      ...prev,
                                      [item.id]: e.target.value
                                    }));
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-full px-3 py-2 text-sm border border-yellow-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 resize-none bg-white"
                                  rows="2"
                                />
                              </div>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="font-bold text-gray-900">
                              {formatPrice(item.line_total != null ? item.line_total : (item.price || 0) * (item.quantity || 0))}
                            </p>
                            <p className="text-sm text-gray-500">
                              {formatPrice(item.price || 0)} each
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="text-gray-600 text-center py-8">No items found in this order.</p>
              )}
            </div>

            {/* Selected Items Summary */}
            {selectedItemIds.size > 0 && (
              <div className="mt-6 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                <h3 className="font-semibold text-indigo-900 mb-2 flex items-center gap-2">
                  <CheckIcon className="w-5 h-5" />
                  Selected Items Summary ({selectedItemIds.size})
                </h3>
                <div className="space-y-2">
                  {Array.from(selectedItemIds).map(itemId => {
                    const item = orderItems.find(i => i.id === itemId);
                    if (!item) return null;
                    return (
                      <div key={itemId} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">
                          {item.name || item.product_name} (Item ID: {item.id})
                        </span>
                        <span className="font-semibold text-gray-900">
                          {formatPrice(item.line_total != null ? item.line_total : (item.price || 0) * (item.quantity || 0))}
                        </span>
                      </div>
                    );
                  })}
                  <div className="pt-2 border-t border-indigo-200 flex items-center justify-between font-bold text-indigo-900">
                    <span>Total Selected:</span>
                    <span>
                      {formatPrice(
                        Array.from(selectedItemIds).reduce((sum, itemId) => {
                          const item = orderItems.find(i => i.id === itemId);
                          return sum + ((item?.price || 0) * (item?.quantity || 0));
                        }, 0)
                      )}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Items Needing Clarification Summary */}
            {itemsNeedingClarification.size > 0 && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
                  <ExclamationTriangleIcon className="w-5 h-5" />
                  Items Requiring Clarification ({itemsNeedingClarification.size})
                </h3>
                <div className="space-y-3">
                  {Array.from(itemsNeedingClarification).map(itemId => {
                    const item = orderItems.find(i => i.id === itemId);
                    if (!item) return null;
                    return (
                      <div key={itemId} className="bg-white p-3 rounded border border-yellow-200">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-gray-700 font-medium text-sm">
                            Item ID: {item.id} - {item.name || item.product_name}
                          </span>
                        </div>
                        {itemNotes[item.id] && (
                          <p className="text-gray-600 text-xs mt-1 italic pl-2 border-l-2 border-yellow-300">
                            {itemNotes[item.id]}
                          </p>
                        )}
                        {!itemNotes[item.id] && (
                          <p className="text-gray-400 text-xs mt-1 italic">No notes added</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-6">
          {/* Shipping Address / Pickup Location */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <MapPinIcon className="w-6 h-6 text-indigo-600" />
              {order.delivery_type === 'STORE_PICKUP' ? 'Pickup Location' : 'Shipping Address'}
            </h2>
            <div className="text-gray-700">
              {order.delivery_type === 'STORE_PICKUP' ? (
                <div className="space-y-1">
                  <p className="font-semibold text-gray-900">{order.outlet_name || 'Store Outlet'}</p>
                  {order.outlet_address_line && <p>{order.outlet_address_line}</p>}
                  {order.outlet_city && order.outlet_state && (
                    <p>{order.outlet_city}, {order.outlet_state} {order.outlet_zip_code}</p>
                  )}
                  {order.outlet_phone && <p>Phone: {order.outlet_phone}</p>}
                  {order.outlet_hours_text && <p className="text-sm text-gray-500">{order.outlet_hours_text}</p>}
                </div>
              ) : typeof shippingAddress === 'object' ? (
                <div className="space-y-1">
                  {shippingAddress.street && <p>{shippingAddress.street}</p>}
                  {shippingAddress.city && shippingAddress.state && (
                    <p>
                      {shippingAddress.city}, {shippingAddress.state} {shippingAddress.zip_code}
                    </p>
                  )}
                  {shippingAddress.country && <p>{shippingAddress.country}</p>}
                </div>
              ) : (
                <p>{shippingAddress}</p>
              )}
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <CreditCardIcon className="w-6 h-6 text-indigo-600" />
              Payment Information
            </h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Payment Method:</span>
                <span className="font-semibold text-gray-900 capitalize">
                  {order.payment_method?.replace('_', ' ') || 'N/A'}
                </span>
              </div>
              {order.payment_status && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Payment Status:</span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      order.payment_status === 'PAID'
                        ? 'bg-green-100 text-green-800'
                        : order.payment_status === 'REFUNDED'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {formatStatus(order.payment_status)}
                  </span>
                </div>
              )}
              {order.delivery_type && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Delivery Type:</span>
                  <span className="font-semibold text-gray-900">
                    {formatStatus(order.delivery_type)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Order Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between text-gray-700">
                <span>Subtotal:</span>
                <span className="font-semibold">
                  {formatPrice(
                    orderItems.reduce((sum, item) => {
                      const price = item.price || 0;
                      const quantity = item.quantity || 0;
                      return sum + (price * quantity);
                    }, 0)
                  )}
                </span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>Shipping:</span>
                <span className="font-semibold text-green-600">Free</span>
              </div>
              <div className="border-t-2 border-gray-200 pt-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900">Total:</span>
                  <span className="text-2xl font-bold text-indigo-600">
                    {formatPrice(order.total_amount || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Tracking Number */}
          {order.tracking_number && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Tracking Information</h2>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 mb-1">Tracking Number</p>
                <p className="font-semibold text-gray-900">{order.tracking_number}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Item Details Modal */}
      {showItemModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            {/* Debug logging */}
            {console.log('🔍 [AdminOrderDetail] Modal - selectedItem:', {
              id: selectedItem.id,
              product_id: selectedItem.product_id,
              barcode: selectedItem.barcode,
              stockcode: selectedItem.stockcode,
              brand: selectedItem.brand,
              department: selectedItem.department,
              category_name: selectedItem.category_name,
              stock_quantity: selectedItem.stock_quantity,
              original_price: selectedItem.original_price,
              discount: selectedItem.discount,
              vat_percentage: selectedItem.vat_percentage
            })}
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <CubeIcon className="w-6 h-6 text-indigo-600" />
                Item Details
              </h2>
              <button
                onClick={() => {
                  setShowItemModal(false);
                  setSelectedItem(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Item Image */}
              <div className="flex justify-center">
                <div className="w-48 h-48 rounded-xl overflow-hidden bg-gray-100 border-2 border-gray-200">
                  <img
                    src={selectedItem.image_url || selectedItem.product_image || '/api/placeholder/200/200'}
                    alt={selectedItem.name || selectedItem.product_name || 'Product'}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/api/placeholder/200/200';
                    }}
                  />
                </div>
              </div>

              {/* Item Information */}
              <div className="space-y-6">
                {/* Product Name and Description */}
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {selectedItem.name || selectedItem.product_name || `Product #${selectedItem.product_id || 'N/A'}`}
                  </h3>
                  {selectedItem.variant_label && (
                    <p className="text-sm text-gray-500 mb-2">{selectedItem.variant_label}</p>
                  )}
                  {selectedItem.description && (
                    <p className="text-gray-600 text-lg leading-relaxed">{selectedItem.description}</p>
                  )}
                </div>

                {/* Primary Information Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                    <p className="text-xs text-indigo-600 mb-1 font-medium uppercase tracking-wide">Product ID</p>
                    <p className="text-lg font-bold text-indigo-900">{selectedItem.product_id || 'N/A'}</p>
                  </div>
                  <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                    <p className="text-xs text-indigo-600 mb-1 font-medium uppercase tracking-wide">Order Item ID</p>
                    <p className="text-lg font-bold text-indigo-900">{selectedItem.id || 'N/A'}</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-600 mb-1 font-medium uppercase tracking-wide">Quantity Ordered</p>
                    <p className="text-lg font-bold text-blue-900">{selectedItem.quantity || 0}</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-600 mb-1 font-medium uppercase tracking-wide">Unit Price</p>
                    <p className="text-lg font-bold text-blue-900">{formatPrice(selectedItem.price || 0)}</p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200 col-span-2">
                    <p className="text-xs text-green-600 mb-1 font-medium uppercase tracking-wide">Total Item Price</p>
                    <p className="text-3xl font-bold text-green-900">
                      {formatPrice((selectedItem.price || 0) * (selectedItem.quantity || 0))}
                    </p>
                  </div>
                </div>

                {/* Product Details Section - Always Show */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <CubeIcon className="w-5 h-5 text-indigo-600" />
                    Product Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Barcode - Always show */}
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1 font-medium uppercase tracking-wide">Barcode</p>
                      <p className="font-semibold text-gray-900 font-mono">{selectedItem.barcode || 'N/A'}</p>
                    </div>
                    
                    {/* Stock Code - Always show */}
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1 font-medium uppercase tracking-wide">Stock Code</p>
                      <p className="font-semibold text-gray-900">{selectedItem.stockcode || 'N/A'}</p>
                    </div>
                    
                    {/* Brand - Always show */}
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1 font-medium uppercase tracking-wide">Brand</p>
                      <p className="font-semibold text-gray-900">{selectedItem.brand || 'N/A'}</p>
                    </div>
                    
                    {/* Department - Always show */}
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1 font-medium uppercase tracking-wide">Department</p>
                      <p className="font-semibold text-gray-900">{selectedItem.department || 'N/A'}</p>
                    </div>
                    
                    {/* Category - Always show */}
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1 font-medium uppercase tracking-wide">Category</p>
                      <p className="font-semibold text-gray-900">{selectedItem.category_name || 'N/A'}</p>
                    </div>
                    
                    {/* Current Stock - Always show */}
                    <div className={`p-4 rounded-lg border ${
                      selectedItem.stock_quantity !== undefined && selectedItem.stock_quantity !== null
                        ? (selectedItem.stock_quantity > 10 
                            ? 'bg-green-50 border-green-200' 
                            : selectedItem.stock_quantity > 0 
                            ? 'bg-yellow-50 border-yellow-200' 
                            : 'bg-red-50 border-red-200')
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                      <p className="text-xs text-gray-600 mb-1 font-medium uppercase tracking-wide">Current Stock</p>
                      <p className={`font-semibold ${
                        selectedItem.stock_quantity !== undefined && selectedItem.stock_quantity !== null
                          ? (selectedItem.stock_quantity > 10 
                              ? 'text-green-900' 
                              : selectedItem.stock_quantity > 0 
                              ? 'text-yellow-900' 
                              : 'text-red-900')
                          : 'text-gray-900'
                      }`}>
                        {selectedItem.stock_quantity !== undefined && selectedItem.stock_quantity !== null 
                          ? `${selectedItem.stock_quantity} units` 
                          : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pricing Information - Always Show */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Pricing Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Original Price - Always show */}
                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <p className="text-xs text-gray-600 mb-1 font-medium uppercase tracking-wide">Original Price</p>
                      <p className="font-semibold text-gray-900">
                        {selectedItem.original_price && parseFloat(selectedItem.original_price) !== parseFloat(selectedItem.price || 0) 
                          ? <span className="line-through">{formatPrice(selectedItem.original_price)}</span>
                          : <span>{formatPrice(selectedItem.price || 0)}</span>}
                      </p>
                    </div>
                    
                    {/* Discount - Always show */}
                    <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                      <p className="text-xs text-red-600 mb-1 font-medium uppercase tracking-wide">Discount</p>
                      <p className="font-semibold text-red-900">
                        {selectedItem.discount && parseFloat(selectedItem.discount) > 0 
                          ? formatPrice(selectedItem.discount) 
                          : '₹0.00'}
                      </p>
                    </div>
                    
                    {/* VAT/GST - Always show */}
                    <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                      <p className="text-xs text-purple-600 mb-1 font-medium uppercase tracking-wide">VAT/GST</p>
                      <p className="font-semibold text-purple-900">
                        {selectedItem.vat_percentage && parseFloat(selectedItem.vat_percentage) > 0 
                          ? `${selectedItem.vat_percentage}%` 
                          : '0%'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Order Context */}
                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Order Context</h4>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-600 mb-1">Order Number</p>
                        <p className="font-semibold text-gray-900">{order?.order_number || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-1">Order Status</p>
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(order?.status)}`}>
                          {formatStatus(order?.status)}
                        </span>
                      </div>
                      {order?.payment_method && (
                        <div>
                          <p className="text-gray-600 mb-1">Payment Method</p>
                          <p className="font-semibold text-gray-900 capitalize">{order.payment_method.replace('_', ' ')}</p>
                        </div>
                      )}
                      {order?.payment_status && (
                        <div>
                          <p className="text-gray-600 mb-1">Payment Status</p>
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                            order.payment_status === 'PAID'
                              ? 'bg-green-100 text-green-800'
                              : order.payment_status === 'REFUNDED'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {formatStatus(order.payment_status)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end gap-3 rounded-b-2xl">
              <button
                onClick={() => {
                  setShowItemModal(false);
                  setSelectedItem(null);
                }}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrderDetail;

